/** @module @liquidefy/code-editor/layout/layout-viewport @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  Position,
  Range,
  ViewportState,
  ViewportId,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  clamp,
  asViewportId,
  generateId,
} from '../code-editor.js';

// ═══ §2 VIEWPORT COMPUTATION — compute, update, containment, visible line/column queries ═══

/**
 * Computes a ViewportState from scroll position, container dimensions, and content metrics.
 */
export function computeViewport(
  scrollTop: number,
  containerHeight: number,
  lineHeight: number,
  totalLines: number,
  charWidth: number,
): EditorResult<ViewportState> {
  if (lineHeight <= 0) return err('INVALID_LINE_HEIGHT', 'lineHeight must be positive');
  if (containerHeight < 0) return err('INVALID_HEIGHT', 'containerHeight must be non-negative');
  if (charWidth <= 0) return err('INVALID_CHAR_WIDTH', 'charWidth must be positive');

  const clampedScrollTop = clamp(scrollTop, 0, Math.max(0, totalLines * lineHeight - containerHeight));
  const startLine = Math.floor(clampedScrollTop / lineHeight);
  const visibleCount = Math.ceil(containerHeight / lineHeight);
  const endLine = Math.min(startLine + visibleCount, totalLines);

  const viewport: ViewportState = {
    id: asViewportId(generateId('vp')),
    startLine,
    endLine,
    startColumn: 0,
    scrollTop: clampedScrollTop,
    scrollLeft: 0,
    width: 0,
    height: containerHeight,
    lineHeight,
    charWidth,
  };

  return ok(viewport, 'VIEWPORT_COMPUTED', 'Viewport computed successfully');
}

/**
 * Returns a new viewport with the specified changes merged in.
 */
export function updateViewport(
  state: ViewportState,
  changes: Partial<Omit<ViewportState, 'id'>>,
): EditorResult<ViewportState> {
  const updated: ViewportState = {
    id: state.id,
    startLine: changes.startLine ?? state.startLine,
    endLine: changes.endLine ?? state.endLine,
    startColumn: changes.startColumn ?? state.startColumn,
    scrollTop: changes.scrollTop ?? state.scrollTop,
    scrollLeft: changes.scrollLeft ?? state.scrollLeft,
    width: changes.width ?? state.width,
    height: changes.height ?? state.height,
    lineHeight: changes.lineHeight ?? state.lineHeight,
    charWidth: changes.charWidth ?? state.charWidth,
  };
  return ok(updated, 'VIEWPORT_UPDATED', 'Viewport updated');
}

/**
 * Checks whether the viewport contains the given line number.
 */
export function viewportContainsLine(viewport: ViewportState, line: number): boolean {
  return line >= viewport.startLine && line < viewport.endLine;
}

/**
 * Checks whether the viewport contains the given position (line + column).
 */
export function viewportContainsPosition(viewport: ViewportState, position: Position): boolean {
  if (position.line < viewport.startLine || position.line >= viewport.endLine) return false;
  const visibleCols = Math.floor(viewport.width / viewport.charWidth);
  if (visibleCols <= 0) return true; // width not set, assume visible
  const startCol = Math.floor(viewport.scrollLeft / viewport.charWidth);
  return position.column >= startCol && position.column < startCol + visibleCols;
}

/**
 * Returns the first fully visible line in the viewport.
 */
export function firstVisibleLine(viewport: ViewportState): number {
  return viewport.startLine;
}

/**
 * Returns the last fully visible line in the viewport.
 */
export function lastVisibleLine(viewport: ViewportState): number {
  const count = Math.floor(viewport.height / viewport.lineHeight);
  return Math.max(viewport.startLine, viewport.startLine + count - 1);
}

/**
 * Returns the number of fully visible lines.
 */
export function visibleLineCount(viewport: ViewportState): number {
  return Math.max(0, Math.floor(viewport.height / viewport.lineHeight));
}

/**
 * Returns the number of fully visible columns.
 */
export function visibleColumnCount(viewport: ViewportState): number {
  if (viewport.charWidth <= 0) return 0;
  return Math.floor(viewport.width / viewport.charWidth);
}

// ═══ §3 SCROLLING — scroll to line, position, by lines/pages, smooth scroll, ensure visible ═══

/**
 * Returns a viewport scrolled so the target line appears at the given alignment.
 */
export function scrollToLine(
  viewport: ViewportState,
  line: number,
  totalLines: number,
  align: 'top' | 'center' | 'bottom' = 'center',
): EditorResult<ViewportState> {
  const clampedLine = clamp(line, 0, Math.max(0, totalLines - 1));
  const maxScrollTop = Math.max(0, totalLines * viewport.lineHeight - viewport.height);
  let targetScrollTop: number;

  switch (align) {
    case 'top':
      targetScrollTop = clampedLine * viewport.lineHeight;
      break;
    case 'center': {
      const centerOffset = Math.floor(viewport.height / 2);
      targetScrollTop = clampedLine * viewport.lineHeight - centerOffset;
      break;
    }
    case 'bottom': {
      targetScrollTop = (clampedLine + 1) * viewport.lineHeight - viewport.height;
      break;
    }
  }

  const finalScrollTop = clamp(targetScrollTop, 0, maxScrollTop);
  const startLine = Math.floor(finalScrollTop / viewport.lineHeight);
  const visCount = Math.ceil(viewport.height / viewport.lineHeight);
  const endLine = Math.min(startLine + visCount, totalLines);

  return ok({
    ...viewport,
    scrollTop: finalScrollTop,
    startLine,
    endLine,
  }, 'SCROLL_TO_LINE', `Scrolled to line ${clampedLine} (${align})`);
}

/**
 * Scrolls to bring the given position into view, using center alignment.
 */
export function scrollToPosition(
  viewport: ViewportState,
  position: Position,
  totalLines: number,
): EditorResult<ViewportState> {
  const vertResult = scrollToLine(viewport, position.line, totalLines, 'center');
  if (!vertResult.success) return vertResult;

  const updated = vertResult.value;
  const scrollLeft = position.column * viewport.charWidth;
  return ok({
    ...updated,
    scrollLeft,
    startColumn: position.column,
  }, 'SCROLL_TO_POSITION', `Scrolled to position ${position.line}:${position.column}`);
}

/**
 * Scrolls by a number of lines (positive = down, negative = up).
 */
export function scrollByLines(
  viewport: ViewportState,
  delta: number,
  totalLines: number,
): EditorResult<ViewportState> {
  const maxScrollTop = Math.max(0, totalLines * viewport.lineHeight - viewport.height);
  const newScrollTop = clamp(viewport.scrollTop + delta * viewport.lineHeight, 0, maxScrollTop);
  const startLine = Math.floor(newScrollTop / viewport.lineHeight);
  const visCount = Math.ceil(viewport.height / viewport.lineHeight);
  const endLine = Math.min(startLine + visCount, totalLines);

  return ok({
    ...viewport,
    scrollTop: newScrollTop,
    startLine,
    endLine,
  }, 'SCROLL_BY_LINES', `Scrolled by ${delta} lines`);
}

/**
 * Scrolls by a number of pages (positive = down, negative = up).
 */
export function scrollByPages(
  viewport: ViewportState,
  delta: number,
  totalLines: number,
): EditorResult<ViewportState> {
  const linesPerPage = visibleLineCount(viewport);
  return scrollByLines(viewport, delta * linesPerPage, totalLines);
}

/**
 * Scrolls to the top of the document.
 */
export function scrollToTop(viewport: ViewportState): EditorResult<ViewportState> {
  const visCount = Math.ceil(viewport.height / viewport.lineHeight);
  return ok({
    ...viewport,
    scrollTop: 0,
    startLine: 0,
    endLine: visCount,
  }, 'SCROLL_TO_TOP', 'Scrolled to top');
}

/**
 * Scrolls to the bottom of the document.
 */
export function scrollToBottom(
  viewport: ViewportState,
  totalLines: number,
): EditorResult<ViewportState> {
  const maxScrollTop = Math.max(0, totalLines * viewport.lineHeight - viewport.height);
  const startLine = Math.floor(maxScrollTop / viewport.lineHeight);
  const visCount = Math.ceil(viewport.height / viewport.lineHeight);
  const endLine = Math.min(startLine + visCount, totalLines);

  return ok({
    ...viewport,
    scrollTop: maxScrollTop,
    startLine,
    endLine,
  }, 'SCROLL_TO_BOTTOM', 'Scrolled to bottom');
}

/**
 * Returns an array of intermediate viewport states for smooth scroll animation.
 * Each step linearly interpolates the scrollTop between current and target.
 */
export function smoothScrollTo(
  viewport: ViewportState,
  targetLine: number,
  steps: number,
): EditorResult<ViewportState[]> {
  if (steps <= 0) return err('INVALID_STEPS', 'Steps must be positive');

  const targetScrollTop = targetLine * viewport.lineHeight;
  const deltaScroll = targetScrollTop - viewport.scrollTop;
  const intermediates: ViewportState[] = [];

  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    // ease-out quad
    const eased = t * (2 - t);
    const currentScrollTop = viewport.scrollTop + deltaScroll * eased;
    const startLine = Math.floor(currentScrollTop / viewport.lineHeight);
    const visCount = Math.ceil(viewport.height / viewport.lineHeight);

    intermediates.push({
      ...viewport,
      scrollTop: currentScrollTop,
      startLine,
      endLine: startLine + visCount,
    });
  }

  return ok(intermediates, 'SMOOTH_SCROLL', `Generated ${steps} scroll frames`);
}

/**
 * Scrolls the minimum amount needed to make the given position visible within the viewport.
 * Optional margin specifies lines of padding around the position.
 */
export function ensurePositionVisible(
  viewport: ViewportState,
  position: Position,
  totalLines: number,
  margin: number = 0,
): EditorResult<ViewportState> {
  const effectiveStart = viewport.startLine + margin;
  const effectiveEnd = lastVisibleLine(viewport) - margin;

  if (position.line >= effectiveStart && position.line <= effectiveEnd) {
    // Already visible, check horizontal
    const startCol = Math.floor(viewport.scrollLeft / viewport.charWidth);
    const visCols = visibleColumnCount(viewport);
    if (visCols <= 0 || (position.column >= startCol && position.column < startCol + visCols)) {
      return ok(viewport, 'ALREADY_VISIBLE', 'Position already visible');
    }
    // Scroll horizontally
    let newScrollLeft = viewport.scrollLeft;
    if (position.column < startCol) {
      newScrollLeft = position.column * viewport.charWidth;
    } else {
      newScrollLeft = (position.column - visCols + 1) * viewport.charWidth;
    }
    return ok({
      ...viewport,
      scrollLeft: Math.max(0, newScrollLeft),
      startColumn: Math.floor(Math.max(0, newScrollLeft) / viewport.charWidth),
    }, 'SCROLL_HORIZONTAL', 'Scrolled horizontally to reveal position');
  }

  // Scroll vertically
  if (position.line < effectiveStart) {
    return scrollToLine(viewport, position.line - margin, totalLines, 'top');
  }
  return scrollToLine(viewport, position.line + margin, totalLines, 'bottom');
}

// ═══ §4 COORDINATE MAPPING — position/line/column to pixel and back ═══

/**
 * Converts a Position (line, column) to pixel coordinates relative to viewport.
 */
export function positionToPixel(
  position: Position,
  viewport: ViewportState,
): EditorResult<{ x: number; y: number }> {
  const y = (position.line - viewport.startLine) * viewport.lineHeight;
  const x = position.column * viewport.charWidth - viewport.scrollLeft;

  return ok({ x, y }, 'POSITION_TO_PIXEL', 'Converted position to pixel');
}

/**
 * Converts pixel coordinates (relative to viewport) to the closest Position.
 * Requires content to clamp column within the line's character count.
 */
export function pixelToPosition(
  x: number,
  y: number,
  viewport: ViewportState,
  content: string,
): EditorResult<Position> {
  const line = pixelYToLine(y, viewport);
  const lines = content.split('\n');
  const clampedLine = clamp(line, 0, Math.max(0, lines.length - 1));

  const rawCol = pixelXToColumn(x, viewport);
  const lineContent = lines[clampedLine] ?? '';
  const clampedCol = clamp(rawCol, 0, lineContent.length);

  return ok(
    { line: clampedLine, column: clampedCol },
    'PIXEL_TO_POSITION',
    'Converted pixel to position',
  );
}

/**
 * Converts a line number to its pixel Y coordinate relative to the viewport top.
 */
export function lineToPixelY(line: number, viewport: ViewportState): number {
  return (line - viewport.startLine) * viewport.lineHeight;
}

/**
 * Converts a pixel Y coordinate (relative to viewport top) to a line number.
 */
export function pixelYToLine(y: number, viewport: ViewportState): number {
  return Math.floor(y / viewport.lineHeight) + viewport.startLine;
}

/**
 * Converts a column number to its pixel X coordinate.
 */
export function columnToPixelX(column: number, viewport: ViewportState): number {
  return column * viewport.charWidth - viewport.scrollLeft;
}

/**
 * Converts a pixel X coordinate to the nearest column number.
 */
export function pixelXToColumn(x: number, viewport: ViewportState): number {
  return Math.round((x + viewport.scrollLeft) / viewport.charWidth);
}

// ═══ §5 VIEWPORT ANALYSIS — position queries, scroll %, overlap ═══

/**
 * Returns true if the viewport is scrolled to the very top.
 */
export function isAtTop(viewport: ViewportState): boolean {
  return viewport.scrollTop <= 0;
}

/**
 * Returns true if the viewport is scrolled to the very bottom.
 */
export function isAtBottom(viewport: ViewportState, totalLines: number): boolean {
  const maxScrollTop = Math.max(0, totalLines * viewport.lineHeight - viewport.height);
  return viewport.scrollTop >= maxScrollTop - 1;
}

/**
 * Returns the scroll percentage (0-100) through the document.
 */
export function scrollPercentage(viewport: ViewportState, totalLines: number): number {
  const maxScrollTop = totalLines * viewport.lineHeight - viewport.height;
  if (maxScrollTop <= 0) return 100;
  return clamp((viewport.scrollTop / maxScrollTop) * 100, 0, 100);
}

/**
 * Returns how many pixels of overscroll exist past the last line.
 * Returns 0 if there is no overscroll.
 */
export function overscrollAmount(viewport: ViewportState, totalLines: number): number {
  const contentHeight = totalLines * viewport.lineHeight;
  const bottomEdge = viewport.scrollTop + viewport.height;
  return Math.max(0, bottomEdge - contentHeight);
}

/**
 * Computes the overlap range between two viewports.
 * Returns null if no overlap exists.
 */
export function viewportOverlap(
  a: ViewportState,
  b: ViewportState,
): Range | null {
  const overlapStart = Math.max(a.startLine, b.startLine);
  const overlapEnd = Math.min(a.endLine, b.endLine);

  if (overlapStart >= overlapEnd) return null;

  return {
    start: { line: overlapStart, column: 0 },
    end: { line: overlapEnd, column: 0 },
  };
}
